import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IActivityLocation, ISearchableBoondocking, ISearchableListing, SearchQuery } from '@curbnturf/objects';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AVAILABLE_SEARCH_API_URL, BASE_API_URL, BOONDOCK_SEARCH_API_URL, SEARCH_API_URL } from '../network/api-config';

/**
 * Local fork of @curbnturf/listing-search's SearchService — trimmed to plain
 * HttpClient and to the one query shape LiveSearchService.findAll() actually
 * uses. The real class depends on CachedHttpClient (@curbnturf/network),
 * which pulls in AlertFacade -> NgRx Store; cnt-landing no longer runs NgRx
 * (see app.config.ts), so that chain threw NG0201 as soon as anything
 * constructed it. Also drops the three native-SQLite DbSets used only for
 * Capacitor offline-cache fallbacks (retrievePoiCached/retrieveListingCached/
 * retrieveBoondockCached) — cnt-landing is web-only.
 */
@Injectable({ providedIn: 'root' })
export class LiveSearchApiService {
  private readonly http = inject(HttpClient);

  /**
   * These services are migrating from bare JSON arrays to a paginated
   * `{ results, total, hasMore }` envelope, and they are not all at the same
   * point in that migration: staging's search *and* boondock routes return the
   * envelope, while production's boondock still returns a bare array. Accepting
   * either shape keeps the client working against every environment instead of
   * being pinned to whichever one it was last written against.
   *
   * The previous code unwrapped `.results` for search only, so once staging's
   * boondock route switched, `boondockListings.map(...)` in
   * LiveSearchService.findAll() threw "map is not a function" — which errored the
   * whole combineLatest and blanked /search plus the home network map.
   */
  private unwrap<T>(response: T[] | { results?: T[] } | null | undefined): T[] {
    if (Array.isArray(response)) return response;
    return response?.results ?? [];
  }

  /** Private listings only (the search service's flattened listing table). For
   *  surfaces that don't need boondocking or POIs, e.g. the home community map;
   *  the POI stream alone is several MB. Degrades to [] on error. */
  findListings(query: SearchQuery): Observable<ISearchableListing[]> {
    return this.findListingsOrFail(query).pipe(catchError(() => of([] as ISearchableListing[])));
  }

  /** findListings() that errors instead of degrading, for callers that must tell "none" from "down". */
  findListingsOrFail(query: SearchQuery): Observable<ISearchableListing[]> {
    return this.http
      .get<ISearchableListing[] | { results: ISearchableListing[]; total: number; hasMore: boolean }>(
        `${SEARCH_API_URL}?${query.toString()}`,
      )
      .pipe(map((response) => this.unwrap(response)));
  }

  /** `listingsQuery` overrides `query` for the private-listing request only, e.g.
   *  to raise its limit without also raising the boondocking one. */
  find(
    query: SearchQuery,
    listingsQuery: SearchQuery = query,
  ): Observable<{
    listings: ISearchableListing[];
    boondockListings: ISearchableBoondocking[];
    poiListings: IActivityLocation[];
  }> {
    const searchString = query.toString();

    // Each stream degrades to [] on its own rather than taking the other two
    // down with it: these three feed a browse surface where partial results are
    // far more useful than a blank page. The callers' error handlers now fire
    // only if every source fails.
    const listings$ = this.findListings(listingsQuery);

    const boondock$ = this.http
      .get<ISearchableBoondocking[] | { results: ISearchableBoondocking[]; total: number; hasMore: boolean }>(
        `${BOONDOCK_SEARCH_API_URL}?${searchString}`,
      )
      .pipe(
        map((response) => this.unwrap(response)),
        catchError(() => of([] as ISearchableBoondocking[])),
      );

    const pois$ = this.http
      .get<IActivityLocation[] | { results: IActivityLocation[] }>(`${BASE_API_URL}activity-location?${searchString}`)
      .pipe(
        map((response) => this.unwrap(response)),
        catchError(() => of([] as IActivityLocation[])),
      );

    return combineLatest([listings$, boondock$, pois$]).pipe(
      map(([listings, boondockListings, poiListings]) => ({ listings, boondockListings, poiListings })),
    );
  }

  /** Date-filtered private-listing search — hits the NestJS server's own
   *  `listing-search` endpoint (real availability, not the flattened
   *  search_listing table `find()`/`SEARCH_API_URL` uses). Requires `query`
   *  to carry both a date range and a bbox (seLat/seLon/nwLat/nwLon) — the
   *  server rejects the request otherwise. */
  findAvailable(query: SearchQuery): Observable<{ listings: ISearchableListing[] }> {
    const searchString = query.toString();

    return this.http.get<{ listings: ISearchableListing[] }>(`${AVAILABLE_SEARCH_API_URL}?${searchString}`);
  }
}
